import React, { useEffect, useState } from "react";
import { ChevronLeft, ChevronRight, ImageIcon } from "lucide-react";

/**
 * Lightweight slideshow that uses Placeholder-style slots until real assets arrive.
 * Each slide is rendered via the `renderSlide` prop to keep it flexible.
 * Optional `autoPlayMs` triggers auto-advance; pauses on hover/touch.
 */
const Slideshow = ({ slides, renderSlide, testId = "slideshow", autoPlayMs = 0 }) => {
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  const total = slides.length;

  const go = (dir) => setIdx((i) => (i + dir + total) % total);

  useEffect(() => {
    if (!autoPlayMs || total <= 1) return;
    const id = setInterval(() => {
      if (paused || document.hidden) return;
      setIdx((i) => (i + 1) % total);
    }, autoPlayMs);
    return () => clearInterval(id);
  }, [autoPlayMs, total, paused]);

  return (
    <div
      data-testid={testId}
      className="relative"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onTouchStart={() => setPaused(true)}
      onTouchEnd={() => setPaused(false)}
    >
      <div className="relative overflow-hidden rounded-2xl">
        {renderSlide(slides[idx], idx)}
      </div>

      {total > 1 && (
        <>
          <button
            data-testid={`${testId}-prev`}
            aria-label="Previous"
            onClick={() => go(-1)}
            className="absolute left-2 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/95 border border-[#F39C12]/30 flex items-center justify-center hover:bg-white shadow"
          >
            <ChevronLeft className="w-5 h-5 text-[#2C3E50]" />
          </button>
          <button
            data-testid={`${testId}-next`}
            aria-label="Next"
            onClick={() => go(1)}
            className="absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/95 border border-[#F39C12]/30 flex items-center justify-center hover:bg-white shadow"
          >
            <ChevronRight className="w-5 h-5 text-[#2C3E50]" />
          </button>

          <div className="mt-4 flex items-center justify-center gap-1.5">
            {slides.map((_, i) => (
              <button
                key={i}
                aria-label={`Slide ${i + 1}`}
                data-testid={`${testId}-dot-${i}`}
                onClick={() => setIdx(i)}
                className={`h-1.5 rounded-full transition-all ${
                  i === idx ? "w-6 bg-[#D63031]" : "w-1.5 bg-[#F39C12]/40"
                }`}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default Slideshow;
export { ImageIcon };
